import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import './PostRFQ.css';

const PostRFQ = (props) => {
  const { user, onSignIn, onNavigateToHome } = props;
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    projectTitle: '',
    category: '',
    description: '',
    deliverables: [''],
    budgetRange: '10000-25000',
    customBudgetMin: '',
    customBudgetMax: '',
    projectDuration: '',
    startDate: '',
    milestones: '',
    skills: [],
    skillInput: '',
    experienceLevel: 'intermediate',
    workLocation: 'remote',
    specificLocation: '',
    freelancersNeeded: '1',
    ndaRequired: false,
    acceptStudents: true,
    backgroundCheck: false,
    preferredComm: 'email',
    responseDeadline: '',
    companyName: '',
    cipcNumber: '',
    contactPerson: '',
    contactEmail: '',
    files: []
  });

  useEffect(() => {
    document.title = 'Post an RFQ | Global Expedyte';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        companyName: user.companyName || '',
        contactPerson: `${user.firstName} ${user.lastName}`,
        contactEmail: user.email || ''
      }));
    }
  }, [user, onSignIn, onNavigateToHome]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const addDeliverable = () => {
    setFormData(prev => ({
      ...prev,
      deliverables: [...prev.deliverables, '']
    }));
  };

  const updateDeliverable = (index, value) => {
    const newDeliverables = [...formData.deliverables];
    newDeliverables[index] = value;
    setFormData(prev => ({
      ...prev,
      deliverables: newDeliverables
    }));
  };

  const removeDeliverable = (index) => {
    if (formData.deliverables.length > 1) {
      const newDeliverables = formData.deliverables.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        deliverables: newDeliverables
      }));
    }
  };

  const addSkill = () => {
    if (formData.skillInput.trim() && !formData.skills.includes(formData.skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, prev.skillInput.trim()],
        skillInput: ''
      }));
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleSkillKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = ['application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg', 'image/png'].includes(file.type);
      const isValidSize = file.size <= 10 * 1024 * 1024;
      return isValidType && isValidSize;
    });

    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...validFiles]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.projectTitle.trim()) {
      newErrors.projectTitle = 'Project title is required';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    if (!formData.description.trim() || formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }

    const validDeliverables = formData.deliverables.filter(d => d.trim());
    if (validDeliverables.length === 0) {
      newErrors.deliverables = 'At least one deliverable is required';
    }

    if (!formData.projectDuration) {
      newErrors.projectDuration = 'Project duration is required';
    }

    if (formData.skills.length === 0) {
      newErrors.skills = 'At least one skill is required';
    }

    if (formData.budgetRange === 'custom') {
      if (!formData.customBudgetMin || !formData.customBudgetMax) {
        newErrors.budget = 'Please specify custom budget range';
      } else if (parseInt(formData.customBudgetMin) >= parseInt(formData.customBudgetMax)) {
        newErrors.budget = 'Maximum budget must be greater than minimum';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadFiles = async (rfqId) => {
    const uploadedUrls = [];
    
    for (let i = 0; i < formData.files.length; i++) {
      const file = formData.files[i];
      const fileRef = ref(storage, `rfqs/${rfqId}/${file.name}`);
      
      try {
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        uploadedUrls.push({
          name: file.name,
          url: url,
          type: file.type,
          size: file.size
        });
        
        setUploadProgress(((i + 1) / formData.files.length) * 100);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }

    setStep(2);
  };

  const confirmSubmit = async () => {
    setLoading(true);
    setUploadProgress(0);

    try {
      let budgetDisplay = '';
      if (formData.budgetRange === 'custom') {
        budgetDisplay = `R${parseInt(formData.customBudgetMin).toLocaleString()} - R${parseInt(formData.customBudgetMax).toLocaleString()}`;
      } else {
        const ranges = {
          '0-5000': 'Under R5,000',
          '5000-10000': 'R5,000 - R10,000',
          '10000-25000': 'R10,000 - R25,000',
          '25000-50000': 'R25,000 - R50,000',
          '50000-100000': 'R50,000 - R100,000',
          '100000-200000': 'R100,000 - R200,000',
          '200000+': 'R200,000+'
        };
        budgetDisplay = ranges[formData.budgetRange];
      }

      const rfqData = {
        title: formData.projectTitle,
        description: formData.description,
        category: formData.category,
        budget: formData.budgetRange === 'custom' 
          ? `${formData.customBudgetMin}-${formData.customBudgetMax}`
          : formData.budgetRange,
        budgetDisplay: budgetDisplay,
        timeline: formData.projectDuration,
        startDate: formData.startDate || null,
        milestones: formData.milestones || null,
        deliverables: formData.deliverables.filter(d => d.trim()),
        skillsRequired: formData.skills,
        experienceLevel: formData.experienceLevel,
        workLocation: formData.workLocation,
        specificLocation: formData.specificLocation || null,
        freelancersNeeded: parseInt(formData.freelancersNeeded),
        ndaRequired: formData.ndaRequired,
        acceptStudents: formData.acceptStudents,
        backgroundCheck: formData.backgroundCheck,
        preferredComm: formData.preferredComm,
        responseDeadline: formData.responseDeadline || null,
        company: formData.companyName,
        cipcNumber: formData.cipcNumber || null,
        contactPerson: formData.contactPerson,
        contactEmail: formData.contactEmail,
        businessId: user?.uid || user?.id || 'test-user',
        status: 'open',
        proposalsCount: 0,
        postedDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'rfqs'), rfqData);

      if (formData.files.length > 0) {
        const uploadedFiles = await uploadFiles(docRef.id);
        
        await addDoc(collection(db, 'rfqs', docRef.id, 'files'), {
          files: uploadedFiles,
          uploadedAt: serverTimestamp()
        });
      }

      setStep(3);
      setLoading(false);

    } catch (error) {
      console.error('Error submitting RFQ:', error);
      alert('Error posting RFQ. Please try again.');
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setStep(1);
    window.scrollTo(0, 0);
  };

  const handleDone = () => {
    if (props.onNavigateToHome) {
      props.onNavigateToHome();
    }
  };

  const getCategoryName = (category) => {
    const categories = {
      'web-development': 'Web Development',
      'graphic-design': 'Graphic Design',
      'bookkeeping': 'Bookkeeping & Accounting',
      'software-development': 'Software Development',
      'digital-marketing': 'Digital Marketing',
      'writing-content': 'Writing & Content',
      'photography-video': 'Photography & Video',
      'it-support': 'IT Support',
      'legal-services': 'Legal Services',
      'data-analytics': 'Data Analytics',
      'consulting': 'Consulting',
      'other': 'Other'
    };
    return categories[category] || category;
  };

  return (
    <div className="post-rfq-page">
      <Navigation {...props} />

      <section className="post-rfq-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            <span>POST AN RFQ</span>
          </div>

          <h1>Find the Perfect Freelancer for Your Project</h1>

          <p className="hero-description">
            Post your project requirements and receive competitive proposals from verified 
            South African freelancers. Compare quotes, review portfolios, and hire with confidence.
          </p>

          <div className="progress-indicator">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Project Details</div>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">Preview</div>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">Published</div>
            </div>
          </div>
        </div>
      </section>

      <div className="post-rfq-content">
        {step === 1 && (
          <form onSubmit={handleSubmit} className="rfq-form">
            <div className="form-container">
              
              <section className="form-section">
                <div className="section-header">
                  <h2>Basic Information</h2>
                  <p>Tell us about your project</p>
                </div>

                <div className="form-group">
                  <label htmlFor="projectTitle">Project Title *</label>
                  <input
                    type="text"
                    id="projectTitle"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    placeholder="e.g., E-commerce Website Development"
                    className={errors.projectTitle ? 'error' : ''}
                  />
                  {errors.projectTitle && <span className="error-message">{errors.projectTitle}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={errors.category ? 'error' : ''}
                  >
                    <option value="">Select a category...</option>
                    <option value="web-development">Web Development</option>
                    <option value="graphic-design">Graphic Design</option>
                    <option value="bookkeeping">Bookkeeping & Accounting</option>
                    <option value="software-development">Software Development</option>
                    <option value="digital-marketing">Digital Marketing</option>
                    <option value="writing-content">Writing & Content</option>
                    <option value="photography-video">Photography & Video</option>
                    <option value="it-support">IT Support</option>
                    <option value="legal-services">Legal Services</option>
                    <option value="data-analytics">Data Analytics</option>
                    <option value="consulting">Consulting</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.category && <span className="error-message">{errors.category}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="description">Project Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="6"
                    placeholder="Provide a detailed description of your project, including goals, requirements, and any specific expectations..."
                    className={errors.description ? 'error' : ''}
                  ></textarea>
                  <div className="char-count">{formData.description.length} characters (minimum 50)</div>
                  {errors.description && <span className="error-message">{errors.description}</span>}
                </div>

                <div className="form-group">
                  <label>Key Deliverables *</label>
                  <p className="field-hint">List the specific outputs you expect from this project</p>
                  {formData.deliverables.map((deliverable, index) => (
                    <div key={index} className="deliverable-item">
                      <input
                        type="text"
                        value={deliverable}
                        onChange={(e) => updateDeliverable(index, e.target.value)}
                        placeholder={`Deliverable ${index + 1}`}
                      />
                      {formData.deliverables.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDeliverable(index)}
                          className="btn-remove"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addDeliverable} className="btn-add">
                    + Add Deliverable
                  </button>
                  {errors.deliverables && <span className="error-message">{errors.deliverables}</span>}
                </div>
              </section>

              <section className="form-section">
                <div className="section-header">
                  <h2>Budget & Timeline</h2>
                  <p>Set your project parameters</p>
                </div>

                <div className="form-group">
                  <label htmlFor="budgetRange">Budget Range (ZAR) *</label>
                  <select
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                  >
                    <option value="0-5000">Under R5,000</option>
                    <option value="5000-10000">R5,000 - R10,000</option>
                    <option value="10000-25000">R10,000 - R25,000</option>
                    <option value="25000-50000">R25,000 - R50,000</option>
                    <option value="50000-100000">R50,000 - R100,000</option>
                    <option value="100000-200000">R100,000 - R200,000</option>
                    <option value="200000+">R200,000+</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {formData.budgetRange === 'custom' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="customBudgetMin">Minimum Budget (R)</label>
                      <input
                        type="number"
                        id="customBudgetMin"
                        name="customBudgetMin"
                        value={formData.customBudgetMin}
                        onChange={handleChange}
                        placeholder="5000"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="customBudgetMax">Maximum Budget (R)</label>
                      <input
                        type="number"
                        id="customBudgetMax"
                        name="customBudgetMax"
                        value={formData.customBudgetMax}
                        onChange={handleChange}
                        placeholder="10000"
                        min="0"
                      />
                    </div>
                  </div>
                )}
                {errors.budget && <span className="error-message">{errors.budget}</span>}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="projectDuration">Project Duration *</label>
                    <select
                      id="projectDuration"
                      name="projectDuration"
                      value={formData.projectDuration}
                      onChange={handleChange}
                      className={errors.projectDuration ? 'error' : ''}
                    >
                      <option value="">Select duration...</option>
                      <option value="less-than-1-week">Less than 1 week</option>
                      <option value="1-2-weeks">1-2 weeks</option>
                      <option value="2-4-weeks">2-4 weeks</option>
                      <option value="1-2-months">1-2 months</option>
                      <option value="2-3-months">2-3 months</option>
                      <option value="3-6-months">3-6 months</option>
                      <option value="6-months-plus">6+ months</option>
                      <option value="ongoing">Ongoing</option>
                    </select>
                    {errors.projectDuration && <span className="error-message">{errors.projectDuration}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="startDate">Preferred Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="milestones">Milestones (Optional)</label>
                  <textarea
                    id="milestones"
                    name="milestones"
                    value={formData.milestones}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe any project milestones or phases..."
                  ></textarea>
                </div>
              </section>

              <section className="form-section">
                <div className="section-header">
                  <h2>Requirements</h2>
                  <p>Specify the skills and experience needed</p>
                </div>

                <div className="form-group">
                  <label htmlFor="skillInput">Required Skills/Tools *</label>
                  <p className="field-hint">Type a skill and press Enter to add</p>
                  <div className="skills-input-container">
                    <input
                      type="text"
                      id="skillInput"
                      name="skillInput"
                      value={formData.skillInput}
                      onChange={handleChange}
                      onKeyPress={handleSkillKeyPress}
                      placeholder="e.g., React, Photoshop, QuickBooks..."
                      className={errors.skills ? 'error' : ''}
                    />
                    <button type="button" onClick={addSkill} className="btn-add-skill">
                      Add
                    </button>
                  </div>
                  
                  <div className="skills-tags">
                    {formData.skills.map((skill, index) => (
                      <div key={index} className="skill-tag">
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="remove-skill"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  {errors.skills && <span className="error-message">{errors.skills}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="experienceLevel">Experience Level *</label>
                    <select
                      id="experienceLevel"
                      name="experienceLevel"
                      value={formData.experienceLevel}
                      onChange={handleChange}
                    >
                      <option value="entry">Entry Level / Student</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="expert">Expert / Senior</option>
                      <option value="agency">Agency / Team</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="freelancersNeeded">Number of Freelancers</label>
                    <input
                      type="number"
                      id="freelancersNeeded"
                      name="freelancersNeeded"
                      value={formData.freelancersNeeded}
                      onChange={handleChange}
                      min="1"
                      max="10"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="workLocation">Work Location *</label>
                  <select
                    id="workLocation"
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleChange}
                  >
                    <option value="remote">Fully Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-site</option>
                    <option value="specific">Specific Location</option>
                  </select>
                </div>

                {(formData.workLocation === 'onsite' || formData.workLocation === 'specific' || formData.workLocation === 'hybrid') && (
                  <div className="form-group">
                    <label htmlFor="specificLocation">Location Details</label>
                    <input
                      type="text"
                      id="specificLocation"
                      name="specificLocation"
                      value={formData.specificLocation}
                      onChange={handleChange}
                      placeholder="e.g., Johannesburg, Cape Town..."
                    />
                  </div>
                )}
              </section>

              <section className="form-section">
                <div className="section-header">
                  <h2>Preferences</h2>
                  <p>Additional requirements and preferences</p>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="ndaRequired"
                      checked={formData.ndaRequired}
                      onChange={handleChange}
                    />
                    <span>NDA Required</span>
                  </label>
                  <p className="field-hint">Freelancers must sign a non-disclosure agreement</p>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptStudents"
                      checked={formData.acceptStudents}
                      onChange={handleChange}
                    />
                    <span>Accept Students/Entry-Level</span>
                  </label>
                  <p className="field-hint">Open to candidates with less experience</p>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="backgroundCheck"
                      checked={formData.backgroundCheck}
                      onChange={handleChange}
                    />
                    <span>Background Check Required</span>
                  </label>
                  <p className="field-hint">Freelancer must provide background verification</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="preferredComm">Preferred Communication</label>
                    <select
                      id="preferredComm"
                      name="preferredComm"
                      value={formData.preferredComm}
                      onChange={handleChange}
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="video">Video Call</option>
                      <option value="platform">Platform Messaging</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="responseDeadline">Proposal Deadline</label>
                    <input
                      type="date"
                      id="responseDeadline"
                      name="responseDeadline"
                      value={formData.responseDeadline}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </section>

              <section className="form-section">
                <div className="section-header">
                  <h2>Supporting Documents</h2>
                  <p>Upload any relevant files (optional but recommended)</p>
                </div>

                <div className="file-upload-area">
                  <input
                    type="file"
                    id="fileUpload"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="fileUpload" className="file-upload-label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <span>Click to upload or drag and drop</span>
                    <span className="file-hint">PDF, DOC, DOCX, JPG, PNG (max 10MB each)</span>
                  </label>
                </div>

                {formData.files.length > 0 && (
                  <div className="uploaded-files">
                    <h4>Uploaded Files ({formData.files.length})</h4>
                    {formData.files.map((file, index) => (
                      <div key={index} className="file-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <div className="file-info">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="btn-remove-file"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="form-section">
                <div className="section-header">
                  <h2>Company Information</h2>
                  <p>Your business details (auto-filled from profile)</p>
                </div>

                <div className="form-group">
                  <label htmlFor="companyName">Company Name *</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your company name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cipcNumber">CIPC Registration Number (Optional)</label>
                  <input
                    type="text"
                    id="cipcNumber"
                    name="cipcNumber"
                    value={formData.cipcNumber}
                    onChange={handleChange}
                    placeholder="2022/123456/07"
                  />
                  <p className="field-hint">Adds credibility to your RFQ</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contactPerson">Contact Person</label>
                    <input
                      type="text"
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="contactEmail">Contact Email</label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </section>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  Preview RFQ
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
              </div>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="rfq-preview">
            <div className="preview-header">
              <h2>Preview Your RFQ</h2>
              <p>Review your RFQ before publishing. This is how freelancers will see it.</p>
            </div>

            <div className="preview-card">
              <div className="preview-section">
                <div className="category-badge">{getCategoryName(formData.category)}</div>
                <h1>{formData.projectTitle}</h1>
                <div className="company-info">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                  </svg>
                  {formData.companyName}
                </div>
              </div>

              <div className="preview-section">
                <h3>Description</h3>
                <p className="description-text">{formData.description}</p>
              </div>

              <div className="preview-section">
                <h3>Key Deliverables</h3>
                <ul className="deliverables-list">
                  {formData.deliverables.filter(d => d.trim()).map((deliverable, index) => (
                    <li key={index}>{deliverable}</li>
                  ))}
                </ul>
              </div>

              <div className="preview-details">
                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  <div>
                    <span className="detail-label">Budget</span>
                    <span className="detail-value">
                      {formData.budgetRange === 'custom'
                        ? `R${parseInt(formData.customBudgetMin).toLocaleString()} - R${parseInt(formData.customBudgetMax).toLocaleString()}`
                        : formData.budgetRange === '0-5000' ? 'Under R5,000'
                        : formData.budgetRange === '5000-10000' ? 'R5,000 - R10,000'
                        : formData.budgetRange === '10000-25000' ? 'R10,000 - R25,000'
                        : formData.budgetRange === '25000-50000' ? 'R25,000 - R50,000'
                        : formData.budgetRange === '50000-100000' ? 'R50,000 - R100,000'
                        : formData.budgetRange === '100000-200000' ? 'R100,000 - R200,000'
                        : 'R200,000+'}
                    </span>
                  </div>
                </div>

                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <div>
                    <span className="detail-label">Duration</span>
                    <span className="detail-value">{formData.projectDuration.replace(/-/g, ' ')}</span>
                  </div>
                </div>

                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <div>
                    <span className="detail-label">Location</span>
                    <span className="detail-value">
                      {formData.workLocation === 'remote' ? 'Fully Remote'
                        : formData.workLocation === 'hybrid' ? `Hybrid - ${formData.specificLocation || 'Location TBD'}`
                        : formData.workLocation === 'onsite' ? `On-site - ${formData.specificLocation || 'Location TBD'}`
                        : formData.specificLocation || 'Flexible'}
                    </span>
                  </div>
                </div>

                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <div>
                    <span className="detail-label">Experience</span>
                    <span className="detail-value">
                      {formData.experienceLevel === 'entry' ? 'Entry Level'
                        : formData.experienceLevel === 'intermediate' ? 'Intermediate'
                        : formData.experienceLevel === 'expert' ? 'Expert / Senior'
                        : 'Agency / Team'}
                    </span>
                  </div>
                </div>
              </div>

              {formData.skills.length > 0 && (
                <div className="preview-section">
                  <h3>Required Skills</h3>
                  <div className="skills-preview">
                    {formData.skills.map((skill, index) => (
                      <span key={index} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {(formData.ndaRequired || formData.backgroundCheck || !formData.acceptStudents) && (
                <div className="preview-section">
                  <h3>Additional Requirements</h3>
                  <ul className="requirements-list">
                    {formData.ndaRequired && <li>NDA Required</li>}
                    {formData.backgroundCheck && <li>Background Check Required</li>}
                    {!formData.acceptStudents && <li>No Entry-Level / Students</li>}
                  </ul>
                </div>
              )}

              {formData.files.length > 0 && (
                <div className="preview-section">
                  <h3>Attached Documents ({formData.files.length})</h3>
                  <div className="files-preview">
                    {formData.files.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <span>{file.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="preview-actions">
              <button onClick={handleEdit} className="btn-secondary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit RFQ
              </button>
              <button onClick={confirmSubmit} className="btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    {uploadProgress > 0 && uploadProgress < 100 
                      ? `Uploading files... ${Math.round(uploadProgress)}%`
                      : 'Publishing...'}
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Publish RFQ
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="rfq-success">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>

            <h1>RFQ Published Successfully!</h1>

            <p className="success-message">
              Your RFQ "<strong>{formData.projectTitle}</strong>" has been published and is now 
              visible to all freelancers on the platform. You'll start receiving proposals soon.
            </p>

            <div className="success-stats">
              <div className="stat-item">
                <div className="stat-number">1000+</div>
                <div className="stat-label">Freelancers can now see your RFQ</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24hrs</div>
                <div className="stat-label">Average time to first proposal</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">5-10</div>
                <div className="stat-label">Expected proposals</div>
              </div>
            </div>

            <div className="success-actions">
              <button onClick={handleDone} className="btn-primary">
                Back to Home
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </button>
              {props.onNavigateToBrowseRFQs && (
                <button onClick={() => props.onNavigateToBrowseRFQs()} className="btn-secondary">
                  Browse All RFQs
                </button>
              )}
            </div>

            <div className="next-steps">
              <h3>What happens next?</h3>
              <ul>
                <li>Freelancers will review your RFQ and submit proposals</li>
                <li>You'll receive email notifications for new proposals</li>
                <li>Compare proposals in your dashboard</li>
                <li>Chat with freelancers before making a decision</li>
                <li>Award the project to your chosen freelancer</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <Footer {...props} />
    </div>
  );
};

export default PostRFQ;